import type { BootstrapConfig, SortBy } from './types';

/**
 * Reads runtime config from the page URL (per §2.8) and from
 * Vite-injected env vars. Pure - safe to call multiple times.
 */
export function readBootstrapConfig(search: string = window.location.search): BootstrapConfig {
  const params = new URLSearchParams(search);

  const storeIdRaw = params.get('storeId');
  const storeId = storeIdRaw ? Number.parseInt(storeIdRaw, 10) : NaN;

  const env = import.meta.env as Record<string, string | undefined>;

  const mockFromUrl = params.get('mock') === '1';
  const mockFromEnv = env.VITE_USE_MOCKS === 'true';

  return {
    storeId,
    xApiKey: params.get('xApiKey'),
    mock: mockFromUrl || mockFromEnv,
    showErrorDetail: env.VITE_SHOW_ERROR_DETAIL === 'true',
    fallbackPollMs: numberOr(env.VITE_FALLBACK_POLL_MS, 15_000),
    reconciliationMs: numberOr(env.VITE_RECONCILIATION_MS, 60_000),
    doneRetentionMinutes: numberOr(env.VITE_DONE_RETENTION_MINUTES, 1),
    appVersionPollMs: numberOr(env.VITE_APP_VERSION_POLL_MS, 5 * 60_000),
    orderLookbackMinutes: numberOr(env.VITE_ORDER_LOOKBACK_MINUTES, 60),
    wsHeartbeatTimeoutMs: numberOr(env.VITE_WS_HEARTBEAT_TIMEOUT_MS, 3 * 60_000),
    sortBy: parseSortBy(params.get('sortBy')),
    scroll: parseFlag(params.get('scroll')),
  };
}

/** Only `priorityScore` is recognised today; anything else falls back to the default sort. */
function parseSortBy(input: string | null): SortBy | null {
  return input === 'priorityScore' ? 'priorityScore' : null;
}

/**
 * Presence-based boolean URL flag. Enabled when the param is present
 * (`?scroll`, `?scroll=1`, `?scroll=true`) and not explicitly turned off
 * (`?scroll=0`, `?scroll=false`).
 */
function parseFlag(input: string | null): boolean {
  if (input === null) return false;
  return input !== '0' && input.toLowerCase() !== 'false';
}

function numberOr(input: string | undefined, fallback: number): number {
  if (!input) return fallback;
  const n = Number(input);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/** Single source of truth for the app version, injected at build time. */
declare const __APP_VERSION__: string;
export const APP_VERSION: string =
  typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'dev';

declare const __SENTRY_DSN__: string;
export const SENTRY_DSN: string | undefined =
  typeof __SENTRY_DSN__ === 'string' && __SENTRY_DSN__ ? __SENTRY_DSN__ : undefined;

export function getOrderLookbackIso(orderLookbackMinutes: number, now = new Date()): string {
  return new Date(now.getTime() - orderLookbackMinutes * 60 * 1000).toISOString();
}
